import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import api, { apiErrorMessage } from "../lib/api";
import { withCache, isNetworkError, queueInspection } from "../lib/offlineStore";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { PhotoUploader } from "../components/PhotoUploader";
import { InspectionResult } from "../components/InspectionResult";
import { APP } from "../constants/testIds";

export default function InspectionForm() {
  const { frontId } = useParams();
  const navigate = useNavigate();
  const [front, setFront] = useState(null);
  const [etat, setEtat] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [purge, setPurge] = useState(false);
  const [commentaire, setCommentaire] = useState("");
  const [ackSuspension, setAckSuspension] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    Promise.all([
      withCache(`front_detail_${frontId}`, () => api.get(`/fronts/${frontId}`).then((r) => r.data)),
      withCache("questions_bimensuelle", () => api.get("/questions?type_inspection=BIMENSUELLE").then((r) => r.data)),
    ]).then(([f, q]) => {
      setFront(f.data.front);
      setEtat(f.data.etat);
      setQuestions(q.data);
    }).catch((err) => {
      setLoadError(err.response?.status === 403 ? "Accès refusé — ce front appartient à un autre site." : "Front introuvable.");
    });
  }, [frontId]);

  const grouped = useMemo(() => {
    const g = {};
    for (const q of questions) {
      g[q.Section] = g[q.Section] || [];
      g[q.Section].push(q);
    }
    return g;
  }, [questions]);

  const setAnswer = (qid, field, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: { ...prev[qid], [field]: value } }));
  };

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.Question_ID]?.reponse);
  const isSuspended = etat?.Statut_Global === "SUSPENDUE";

  const handleSubmit = async () => {
    setError("");
    setSubmitting(true);
    const reponses = questions.map((q) => ({
      question_id: q.Question_ID,
      reponse: answers[q.Question_ID]?.reponse,
      commentaire: answers[q.Question_ID]?.commentaire || "",
      photo_url: (answers[q.Question_ID]?.photos || [])[0] || "",
    }));
    const payload = { front_id: frontId, type_inspection: "BIMENSUELLE", reponses, purge_effectuee: purge, commentaire };
    try {
      const { data } = await api.post("/inspections", payload);
      setResult(data);
    } catch (err) {
      if (isNetworkError(err)) {
        queueInspection(payload);
        setResult({ offline: true });
      } else {
        setError(apiErrorMessage(err.response?.data?.detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return <div data-testid="inspection-load-error" className="min-h-[60vh] flex items-center justify-center text-red-400 font-mono uppercase text-sm text-center px-4">{loadError}</div>;
  }

  if (!front || !etat) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500 font-mono uppercase text-sm">Chargement...</div>;
  }

  if (result) {
    return <InspectionResult result={result} backTo={`/front/${frontId}`} />;
  }

  if (isSuspended && !ackSuspension) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 space-y-5">
        <div className="rounded-2xl border-2 border-red-600 bg-red-950/80 p-6 space-y-3 text-center">
          <AlertTriangle className="w-10 h-10 text-red-300 mx-auto" />
          <h1 className="text-xl font-black uppercase text-red-200">⚠ Attention</h1>
          <p className="text-red-100">Ce front est actuellement <b>SUSPENDU</b>.</p>
          <p className="text-sm text-red-300">Dernière mise à jour : {etat.Derniere_Mise_A_Jour ? new Date(etat.Derniere_Mise_A_Jour).toLocaleString("fr-FR") : "—"}</p>
          <div className="flex flex-col gap-2 pt-2">
            <Button data-testid="view-suspension-button" variant="outline" onClick={() => navigate(`/front/${frontId}?tab=historique`)} className="border-red-400 text-red-200">
              Consulter la suspension
            </Button>
            <Button data-testid="ack-suspension-continue-button" onClick={() => setAckSuspension(true)} className="bg-slate-100 text-red-900 font-bold uppercase">
              Continuer l'inspection malgré tout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-500 mb-1">Inspection bimensuelle des fronts</div>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-50">{front.Front_Libelle}</h1>
        {isSuspended && (
          <div className="mt-2 inline-flex items-center gap-2 text-xs font-bold uppercase text-red-300 bg-red-500/10 border border-red-500/40 rounded-full px-3 py-1">
            <ShieldAlert className="w-3.5 h-3.5" /> Front actuellement suspendu
          </div>
        )}
      </div>

      {Object.entries(grouped).map(([section, qs]) => (
        <div key={section} className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-4">
          <h3 className="text-sm font-black uppercase tracking-widest text-cyan-400">{section}</h3>
          {qs.map((q) => {
            const ans = answers[q.Question_ID] || {};
            const isDangerAnswer = ans.reponse && ans.reponse === q.Reponse_Dangereuse;
            const showExtra = isDangerAnswer && (q.Commentaire_si_Anomalie === "TRUE" || q.Photo_si_Anomalie === "TRUE");
            return (
              <div key={q.Question_ID} className="space-y-2 border-b border-slate-800/60 pb-4 last:border-0 last:pb-0">
                <p className="text-sm text-slate-200">
                  {q.Question} {q.Criticite_Anomalie === "CRITIQUE" && <span className="text-red-400 text-xs font-bold ml-1">[CRITIQUE]</span>}
                </p>
                {q.Aide_Terrain && <p className="text-xs text-slate-500 italic">{q.Aide_Terrain}</p>}
                <div className="flex gap-2 flex-wrap">
                  {(q.options_list || []).map((opt) => {
                    const isActive = ans.reponse === opt;
                    const isConforme = opt === q.Reponse_Conforme;
                    const isDanger = opt === q.Reponse_Dangereuse;
                    const activeClass = isConforme ? "bg-emerald-500 text-black border-emerald-500" : isDanger ? "bg-red-600 text-white border-red-600" : "bg-slate-600 text-white border-slate-600";
                    return (
                      <button
                        key={opt}
                        type="button"
                        data-testid={APP.checklistItemToggle(q.Question_ID, opt)}
                        onClick={() => setAnswer(q.Question_ID, "reponse", opt)}
                        className={`rounded-lg border-2 px-5 py-2.5 text-xs font-bold uppercase min-h-[48px] transition-colors ${
                          isActive ? activeClass : "border-slate-700 text-slate-400 hover:border-slate-500"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {showExtra && (
                  <div className="pl-1 space-y-2 pt-1">
                    {q.Commentaire_si_Anomalie === "TRUE" && (
                      <Textarea
                        data-testid={`inspection-comment-${q.Question_ID}`}
                        placeholder="Commentaire / description de l'anomalie"
                        value={ans.commentaire || ""}
                        onChange={(e) => setAnswer(q.Question_ID, "commentaire", e.target.value)}
                        className="bg-[#0A0D12] border-slate-700 text-slate-100 text-sm"
                      />
                    )}
                    {q.Photo_si_Anomalie === "TRUE" && (
                      <PhotoUploader photos={ans.photos || []} onChange={(p) => setAnswer(q.Question_ID, "photos", p)} testId={`inspection-photos-${q.Question_ID}`} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="rounded-xl border border-slate-800 bg-[#121820] p-5 space-y-3">
        <label className="flex items-center gap-2.5 text-sm text-slate-200 cursor-pointer">
          <Checkbox data-testid="inspection-purge-checkbox" checked={purge} onCheckedChange={setPurge} />
          Purge réalisée lors de cette inspection
        </label>
        <Textarea
          data-testid="inspection-general-comment"
          placeholder="Commentaire général (optionnel)"
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          className="bg-[#0A0D12] border-slate-700 text-slate-100 text-sm"
        />
      </div>

      {error && <div className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-lg px-3 py-2">{error}</div>}

      <Button
        data-testid="inspection-submit-button"
        disabled={!allAnswered || submitting}
        onClick={handleSubmit}
        className="w-full h-12 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-black font-bold uppercase tracking-wide"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Valider l'inspection"}
      </Button>
    </div>
  );
}
